/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import { useCommerceAPI } from '../contexts';
import useCustomer from './useCustomer';
import { GIFT_CERTIFICATE, isError } from '../utils';
import {
    Basket,
    CustomerInfo,
    Order,
    OrderAddress,
    Product,
    ProductItem,
    ProductItemBasket,
} from '~/lib/data-contract/salesforce';
import { useMarket } from '~/shared/utils';
import { useBasketStore } from '~/features/basket/hooks/useBasket';
import {
    getRandomTrackingValue,
    getTrackingCookieValue,
    serializeUserAgentWithTimestamp,
    TrackingCookie,
} from '~/shared/utils/getTrackingCookieValue';
import { useTranslation } from '~/shared/utils/translation';
import { useErrorHandler } from '~/shared/utils/errorBoundary/hooks/useErrorHandler';
import { notifyError } from '~/shared/utils/errorBoundary/toast';

/**
 * TEMP(IMC-558): This is our expected shape of the MultiError if we get CustomerBasketsQuotaExceededException.
 * Remove once GET /baskets is working.
 */
type CustomerBasketsQuotaExceededException = {
    status: number;
    _type: string;
    _data: {
        basketIds: string | string[];
    };
};

/**
 * TEMP(IMC-558): This is a check to see if we're getting the right shape of error.
 * Remove once GET /baskets is working.
 */
const isCustomerBasketsQuotaExceededException = (
    error: unknown,
): error is CustomerBasketsQuotaExceededException =>
    !!(error as CustomerBasketsQuotaExceededException)._data &&
    !!(error as CustomerBasketsQuotaExceededException)._data.basketIds &&
    // Checks for both Array and String.
    !!(error as CustomerBasketsQuotaExceededException)._data.basketIds.length;

export default function useBasket(opts: { currency?: string } = {}) {
    const { currency } = opts;
    const api = useCommerceAPI();
    const customer = useCustomer();
    const { translate } = useTranslation();
    const { trackAndDisplayError: trackError } = useErrorHandler();
    const { isoCulture } = useMarket();
    const { basket, setBasket: updateBasket } = useBasketStore((store) => ({
        basket: store.basket,
        setBasket: store.setBasket,
    }));

    const setBasket = (basketData: Basket | undefined) => {
        const _productItemsDetail = basket?._productItemsDetail;
        if (basketData?.basketId) {
            localStorage.setItem('basketId', basketData?.basketId);
        }
        updateBasket({ _productItemsDetail, ...basketData });
    };

    // Check if a this represents a valid basket
    const loaded = !!basket && !!basket.basketId;

    const itemCount = () => basket?.productItems?.length || 0;

    /** Items taking into account quantity of each */
    const itemAccumulatedCount = () => {
        return basket?.productItems?.reduce((prev, next) => prev + next.quantity!, 0) || 0;
    };

    /** Sum of all order-level discounts */
    const totalOrderDiscount = () => {
        const total = basket?.productItems?.reduce((acc, item: ProductItem) => {
            const price =
                item?.priceAdjustments?.reduce(
                    (acc: number, price) => (acc += Math.abs(price.price ?? 0)),
                    0,
                ) ?? 0;

            acc.push(price);
            return acc;
        }, []);

        return total?.reduce((a: number, b: number) => a + b, 0) ?? 0;
    };

    /**
     * Get an existing basket, if basket doesn't exist, create a new one.
     *
     * NOTE: This request is the only time we are using the ShopperCustomers API
     * to interact with a customer basket. All other calls are done through the
     * ShopperBaskets API, which in our case, uses OCAPI rather than commerce sdk.
     */
    async function getOrCreateBasket() {
        let basket: Basket | undefined;
        if (customer.isStorePersonnel) {
            if (api.auth?.basketId) {
                try {
                    const req = (await api.shopperBaskets?.getBasket({
                        parameters: { basketId: api.auth?.basketId },
                    })) as Basket;
                    if (isError(req)) {
                        throw new Error(JSON.stringify(req));
                    }
                    basket = req;
                } catch {
                    console.debug('Could not find basket with id, creating new');
                }
            }
        } else {
            const customerBaskets = await api.shopperCustomers?.getCustomerBaskets({
                parameters: { customerId: customer.customerId! },
            });

            // Throw if there was a problem getting the customer baskets
            if (isError(customerBaskets)) {
                throw new Error(JSON.stringify(customerBaskets));
            }

            // We only support single baskets for now. Grab the first one.
            basket = Array.isArray(customerBaskets?.baskets)
                ? (customerBaskets?.baskets[0] as Basket)
                : undefined;
        }

        if (!basket) {
            // Back to using ShopperBaskets for all basket interaction.
            let basketProps = {};

            if (customer.isStorePersonnel) {
                await api.auth?.requestInstoreAgentToken();
                basketProps = {
                    body: {
                        c_agentStoreID: customer.c_preferredStore,
                    },
                };
            }

            // TODO(IMC-514): Remove @ts-ignore lines below.
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            //@ts-ignore
            basket = await api.shopperBaskets
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                //@ts-ignore
                ?.createBasket(basketProps)
                .then((data) => data)
                /**
                 * TEMP(IMC-558): Due to an unknown issue on the GET /baskets endpoint in SFCC, we are not getting the baskets
                 * of customers. However, when creating a basket, we're getting a "CustomerBasketsQuotaExceededException" error.
                 * This seems to be because we already have an existing basket.
                 *
                 * If we get this error, we'll use the basket defined on basketIds and try and fetch that.
                 * Remove once GET /baskets is working.
                 */
                .catch(async (error) => {
                    if (isCustomerBasketsQuotaExceededException(error)) {
                        const basketIds = error._data.basketIds;

                        // Skip processing if we don't get basketIds.
                        if (!basketIds.length) return error;

                        const existingBasket = await api.shopperBaskets?.getBasket({
                            parameters: {
                                basketId: Array.isArray(basketIds) ? basketIds[0] : basketIds,
                            },
                        });

                        return existingBasket;
                    }
                });

            // Throw if there was a problem creating the basket
            if (isError(basket)) {
                throw new Error(JSON.stringify(basket));
            }

            if (customer.isStorePersonnel) {
                api.auth?.saveBasketId(basket?.basketId);
            }
        }

        // Update basket currency if it was created with the wrong one, this will also set the state.
        if (currency && basket?.currency !== currency) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            await updateBasketCurrency(currency, basket!.basketId!);
        } else {
            setBasket(basket);
        }

        return basket;
    }

    /**
     * Update the currency of the basket
     * @param currency - The currency code.
     * @param basketID - The id of the basket.
     * @returns {Promise<void>}
     */
    async function updateBasketCurrency(currency: string, basketId: string) {
        const updateBasket = (await api.shopperBaskets?.updateBasket({
            body: { currency },
            parameters: { basketId },
        })) as Basket;
        if (isError(updateBasket)) {
            throw new Error(JSON.stringify(updateBasket));
        } else {
            setBasket(updateBasket);
        }
    }

    /**
     * Asynchronously updates the tracking IDs of the specified basket.
     * The tracking IDs include the Google Analytics Client ID, Google Analytics Session ID,
     * Facebook Pixel ID and Facebook click ID. The function first attempts to retrieve these
     * values from their respective cookies. If any of these cookies do not exist, fallback
     * values are used: either a serialized UserAgent with timestamp or a randomly generated value.
     *
     * @param {string} basketId - The ID of the basket to be updated.
     * @returns {Promise<void>}
     */
    async function updateBasketTrackingIds(basketId: string) {
        try {
            // Try to get the Google Analytics Client ID cookie.
            // If the cookie does not exist, gaClientIdCookie will be null.
            const gaClientIdCookie = getTrackingCookieValue({
                key: TrackingCookie.GA,
                wildcardSearch: false,
                regex: /(\d{5,})\.(\d{5,})/,
            });

            // Try to get the Google Analytics Session ID cookie.
            // The regex is used to capture the first sequence of numbers greater than 4 digits after a dot (.).
            // If the cookie does not exist, gaSessionIdCookie will be null.
            const gaSessionIdCookie = getTrackingCookieValue({
                key: `${TrackingCookie.GA}_2KFZHJLFZD`,
                wildcardSearch: false,
                regex: /(\d{5,})/,
            });

            // Try to get the Facebook Pixel ID cookie.
            // If the cookie does not exist, fbpCookie will be null.
            const fbpCookie = getTrackingCookieValue({
                key: TrackingCookie.FBP,
                wildcardSearch: false,
            });

            // Try to get the Facebook click ID cookie.
            // If the cookie does not exist, fbcCookie will be null.
            const fbcCookie = getTrackingCookieValue({
                key: TrackingCookie.FBC,
                wildcardSearch: false,
            });

            const gclAUCookie = getTrackingCookieValue({
                key: TrackingCookie.GCLAU,
                wildcardSearch: false,
                regex: /(\d{5,})\.(\d{5,})/,
            });

            const gclAWCookie = getTrackingCookieValue({
                key: TrackingCookie.GCLAW,
                wildcardSearch: false,
            });

            const gclIDCookie = getTrackingCookieValue({
                key: TrackingCookie.GCLID,
                wildcardSearch: false,
            });

            // If gaClientIdCookie exists, use its value, else use serialized UserAgent with timestamp.
            const gaClientId = gaClientIdCookie
                ? gaClientIdCookie
                : serializeUserAgentWithTimestamp();

            // If gaSessionIdCookie exists, use its value, else use a randomly generated value.
            const gaSessionId = gaSessionIdCookie ? gaSessionIdCookie : getRandomTrackingValue();

            // If fbpCookie exists, use its value, else assign it as false.
            const fbpId = fbpCookie ? fbpCookie : '';

            // If fbcCookie exists, use its value, else assign it as false.
            const fbcId = fbcCookie ? fbcCookie : '';

            const gclAU = gclAUCookie ? gclAUCookie : '';

            const gclAW = gclAWCookie ? gclAWCookie : '';

            const gclID = gclIDCookie ? gclIDCookie : '';

            // Call the updateBasket API to update the basket with the retrieved tracking IDs.
            const updateBasket = await api.shopperBaskets?.updateBasket({
                body: {
                    c_trackingClientID: gaClientId,
                    c_trackingSessionID: gaSessionId,
                    c_trackingFbID: fbpId,
                    c_trackingFbClid: fbcId,
                    c_gclAU: gclAU,
                    c_gclAW: gclAW,
                    c_gclID: gclID,
                },
                parameters: { basketId },
            });

            // If the updateBasket API call returns an error, throw an error with the error message.
            if (isError(updateBasket)) {
                throw new Error(JSON.stringify(updateBasket));
            } else {
                // If the API call is successful, update the basket in the application state.
                setBasket(updateBasket as Basket);
            }
        } catch (error) {
            // If an error occurs during the function execution, log it to the console.
            console.error('An error occurred while updating the basket tracking IDs: ', error);
        }
    }

    /**
     * Add an item to the basket.
     *
     * @param {array} item
     * @param {string} item.productId - The id of the product.
     * @param {number} item.quantity - The quantity of the item.
     */
    async function addItemToBasket(item: ProductItemBasket[]) {
        if (!basket?.basketId) return;

        const response = (await api.shopperBaskets?.addItemToBasket({
            body: item,
            parameters: { basketId: basket.basketId },
        })) as Basket;
        if (response?.fault) {
            throw new Error(JSON.stringify(response));
        } else {
            setBasket(response);
        }

        return response;
    }

    //     /**
    //      * Remove an item from the basket.
    //      *
    //      * @param {string} itemId - The id of the basket item.
    //      */
    async function removeItemFromBasket(itemId: string) {
        if (!basket?.basketId) return;

        const response = (await api.shopperBaskets?.removeItemFromBasket({
            parameters: { basketId: basket.basketId, itemId: itemId },
        })) as Basket;

        if (response?.fault) {
            throw new Error(JSON.stringify(response));
        }

        const { productId } = basket?.productItems?.find((item) => item.itemId === itemId) ?? {};

        if (productId && productId in basket?._productItemsDetail) {
            delete basket?._productItemsDetail[productId];
        }

        setBasket(response);
        return response;
    }

    //     /**
    //      * Update an item in the basket.
    //      *
    //      * @param {object} item
    //      * @param {string} item.productId - The id of the product.
    //      * @param {number} item.quantity - The quantity of the item.
    //      * @param {string} basketItemId - The id of the item.
    //      */
    async function updateItemInBasket(item: ProductItemBasket, basketItemId: string) {
        if (!basket?.basketId) return;
        try {
            const response = (await api.shopperBaskets?.updateItemInBasket({
                body: item,
                parameters: { basketId: basket.basketId, itemId: basketItemId },
            })) as Basket;
            if (response?.fault) {
                throw new Error(JSON.stringify(response));
            } else {
                setBasket(response);
            }
            return response;
        } catch (e: any) {
            if (e.type === 'ProductItemNotAvailableException') {
                updateItemInBasket({ ...item, quantity: 0 }, basketItemId);
                trackError(translate('message.productItemNotAvailableException'));
            } else {
                trackError(translate('message.generalErrorMessage'));
            }
        }
    }

    const getSelectedQuantity = (itemId: string) =>
        basket?.productItems?.find((p) => p.productId === itemId)?.quantity || 0;

    /**
     * Get the product information for all items in the basket.
     *
     * @param {string} ids - The id(s) of the product, separated by ",".
     * @param {boolean} options - options to pass as params to the api request
     */
    async function getProductsInBasket(
        ids: string,
        options: {
            organizationId: string;
            inventoryIds?: string;
            currency?: string;
            locale?: string;
            allImages?: boolean;
            perPricebook?: boolean;
            siteId: string;
        },
    ) {
        if (!ids) {
            return;
        }

        const splitIdsIntoChunks = (ids: string, chunkSize: number) => {
            const idArray = ids.split(',');
            const chunks: string[][] = [];

            for (let i = 0; i < idArray.length; i += chunkSize) {
                chunks.push(idArray.slice(i, i + chunkSize));
            }

            return chunks;
        };

        // Helper function to split ids into chunks as Salesforce can handle up to max 24ids in one API-call
        const idChunks = splitIdsIntoChunks(ids, 24);

        let itemDetail: Record<string, Product> = {};

        for (const idChunk of idChunks) {
            const response = await api.shopperProducts?.getProducts({
                parameters: { ids: idChunk.join(','), locale: isoCulture, currency, ...options },
            });

            const chunkItemDetail = response?.data.reduce((result, item) => {
                result[item.id] = item;
                return result;
            }, {} as Product);

            itemDetail = { ...chunkItemDetail };
        }

        const updatedBasket = {
            ...basket,
            _productItemsDetail: { ...basket?._productItemsDetail, ...itemDetail },
        };

        setBasket(updatedBasket);
        return updatedBasket;
    }

    async function getProductInBasket(
        id: string,
        options: {
            organizationId: string;
            inventoryIds?: string;
            currency?: string;
            locale?: string;
            allImages?: boolean;
            perPricebook?: boolean;
            siteId: string;
        },
    ) {
        if (!id) {
            return;
        }

        const response = await api.shopperProducts?.getProducts({
            parameters: { ids: id, locale: isoCulture, currency, ...options },
        });

        return response?.data;
    }

    /**
     * Set the shipping address for the current basket.
     * @external Address
     * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#orderaddress
     */
    async function setShippingAddress(
        address: OrderAddress | Record<string, unknown>,
        shipmentId = '',
    ) {
        if (!basket?.basketId) return;
        const response = (await api.shopperBaskets
            ?.updateShippingAddressForShipment({
                body: address,
                parameters: {
                    basketId: basket.basketId,
                    shipmentId,
                    useAsBilling: !basket.billingAddress,
                },
            })
            .catch(() => {
                if (translate('checkout.updateShippingAddressError')) {
                    notifyError(new Error(translate('checkout.updateShippingAddressError')));
                }
            })) as Basket;

        setBasket(response);
        return response;
    }

    /**
     * Set the shipping method for the current basket.
     *
     * @param {string} id - The id of the shipping method.
     */
    async function setShippingMethod(
        id: string,
        shipmentId = '',
        storeId?: string | undefined,
        deliveryDate?: string,
    ) {
        if (!basket?.basketId || !shipmentId) return;
        const response = (await api.shopperBaskets
            ?.updateShippingMethodForShipment({
                body: { id, storeId, deliveryDate },
                parameters: { basketId: basket.basketId, shipmentId },
            })
            .catch(() => {
                if (translate('checkout.updateShippingMethodError')) {
                    notifyError(new Error(translate('checkout.updateShippingMethodError')));
                }
            })) as Basket;

        setBasket(response);
    }

    /**
     * Set the billing address for the current basket.
     * @external Address
     * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#orderaddress
     */
    async function setBillingAddress(address: OrderAddress, params: { useAsShipping?: boolean }) {
        if (!basket?.basketId) return;
        const response = (await api.shopperBaskets
            ?.updateBillingAddressForBasket({
                body: address,
                parameters: { basketId: basket.basketId, ...params },
            })
            .catch(() => {
                if (translate('checkout.updateBillingAddressError')) {
                    notifyError(new Error(translate('checkout.updateBillingAddressError')));
                }
            })) as Basket;

        setBasket(response);
        return response;
    }

    //     /**
    //      * Set the payment instrument for the current basket
    //      *
    //      * NOTE: API does allow adding multiple payment instruments to split payment. However,
    //      * we are currently only handling a single payment instrument.
    //      * Commerce API does not have an endpoint to edit a payment instrument, but OCAPI does.
    //      * We want to emulate Commerce API behavior (when using OCAPI) so we'll just remove the
    //      * existing payment and add the new one to simulate editing. We're making an assumption
    //      * that our basket will never have more than one payment instrument applied at any time.
    //      *
    //      * @external PaymentInstrument
    //      * @see https://salesforcecommercecloud.github.io/commerce-sdk-isomorphic/modules/shopperbaskets.html#basketpaymentinstrumentrequest
    //      */
    //     async setPaymentInstrument(paymentInstrument: BasketPaymentInstrumentRequest) {
    //         if (!basket?.basketId) return;

    //         // Keep reference to existing payment instrument id
    //         const existingPaymentInstrumentId =
    //             basket.paymentInstruments && basket.paymentInstruments[0]?.paymentInstrumentId;

    //         // Add the new payment instrument to basket
    //         let response = await api.shopperBaskets?.addPaymentInstrumentToBasket({
    //             body: paymentInstrument,
    //             parameters: { basketId: basket.basketId },
    //         });

    //         // TODO: Handle possible error when adding payment instrument.
    //         // We won't attempt to remove the previous payment instrument in this case.

    //         // Remove the previous payment instrument if it existed
    //         if (existingPaymentInstrumentId) {
    //             response = await api.shopperBaskets?.removePaymentInstrumentFromBasket({
    //                 parameters: {
    //                     basketId: basket.basketId,
    //                     paymentInstrumentId: existingPaymentInstrumentId,
    //                 },
    //             });
    //         }

    //         setBasket(response);
    //     },

    //     /**
    //      * Remove the payment instrument for the current basket
    //      */
    //     async removePaymentInstrument() {
    //         if (!basket?.basketId) return;
    //         const paymentInstrumentId =
    //             basket.paymentInstruments && basket.paymentInstruments[0]?.paymentInstrumentId;

    //         if (!paymentInstrumentId) {
    //             return;
    //         }

    //         const response = await api.shopperBaskets?.removePaymentInstrumentFromBasket({
    //             parameters: {
    //                 basketId: basket.basketId,
    //                 paymentInstrumentId: paymentInstrumentId,
    //             },
    //         });

    //         setBasket(response);
    //     },

    //     /**
    //      * Update the customer information for the current basket.
    //      *
    //      * @param {object} info
    //      * @param {string} info.email - The email of the customer.
    //      */

    async function approvePaymentInstrument(
        orderNo: string,
        paymentInstrumentId: string,
        paymentMethodId: string,
    ) {
        return await api.shopperBaskets?.updatePaymentInstrumentToBasket({
            body: { paymentMethodId: paymentMethodId },
            parameters: { orderNo, paymentInstrumentId },
        });
    }

    async function updateCustomerInfo(info: CustomerInfo) {
        if (!basket?.basketId) return;
        const response = (await api.shopperBaskets?.updateCustomerForBasket({
            body: info,
            parameters: { basketId: basket.basketId },
        })) as Basket;

        setBasket(response);
    }

    /**
     * Apply a coupon/promo code to the current basket
     * @param {string} code - The promo code to be applied
     */
    async function applyPromoCode(code: string) {
        if (!basket?.basketId) return;
        const response = (await api.shopperBaskets?.addCouponToBasket({
            body: { code },
            parameters: { basketId: basket.basketId },
        })) as Basket;

        if (response?.fault) {
            throw new Error(JSON.stringify(response));
        }

        setBasket(response);
        return response;
    }

    /**
     * Remove a coupon/promo code from the current basket
     * @param {string} couponIemId - The item id of the appied code
     */
    async function removePromoCode(couponItemId: string) {
        if (!basket?.basketId) return;

        const response = (await api.shopperBaskets?.removeCouponFromBasket({
            parameters: { basketId: basket.basketId, couponItemId },
        })) as Basket;

        if (response?.fault) {
            throw new Error(JSON.stringify(response));
        }

        setBasket(response);
        return response;
    }

    async function addGiftCertificatePayment(code: string, cvc?: string) {
        if (!basket?.basketId) return;

        const response = (await api.shopperBaskets?.addPaymentInstrumentToBasket({
            parameters: { basketId: basket.basketId },
            body: {
                paymentMethodId: GIFT_CERTIFICATE,
                giftCertificateCode: code,
                c_paymentData: cvc?.length ? JSON.stringify({ security_code: cvc }) : undefined,
            },
        })) as Basket;

        if (response?.fault) {
            throw new Error(JSON.stringify(response));
        }

        setBasket(response);
    }
    async function removeGiftCertificatePayment(paymentInstrumentId: string) {
        if (!basket?.basketId) return;

        const response = (await api.shopperBaskets?.removePaymentInstrumentFromBasket({
            parameters: { basketId: basket.basketId, paymentInstrumentId },
        })) as Basket;

        if (response?.fault) {
            throw new Error(JSON.stringify(response));
        }

        setBasket(response);
    }

    // async function addGiftCertificateItemToBasket() {
    //     if (!basket?.basketId) return;

    //     // TODO: To be enabled when we do creation of gift cards

    //     const response = await api.shopperBaskets?.addGiftCertificateItemToBasket({
    //         parameters: { basketId: basket.basketId },
    //         body: {},
    //     }) as Basket;

    //     if (response?.fault) {
    //         throw new Error(JSON.stringify(response));
    //     }

    //     setBasket(response);
    // }

    // async function removeGiftCertificateItemFromBasket(code: string) {
    //     if (!basket?.basketId) return;

    //     const response = await api.shopperBaskets?.removeGiftCertificateItemFromBasket({
    //         parameters: { basketId: basket.basketId, giftCertificateItemId: code },
    //     }) as Basket;

    //     if (response?.fault) {
    //         throw new Error(JSON.stringify(response));
    //     }

    //     setBasket(response);
    // }

    /**
     * Fetches and returns promo details for the given IDs
     * @param {Array<string>} ids - The promo ids to fetch
     * @returns {Object} - API response containing data
     */
    async function getPromotions(ids: string[]) {
        const response = await api.shopperPromotions?.getPromotions({
            parameters: { ids: ids.join(',') },
        });

        return response;
    }

    /**
     * Creates an order using the current basket.
     */
    async function createOrder() {
        if (!basket?.basketId) return;
        const response = await api.shopperOrders?.createOrder({
            body: { basketId: basket.basketId },
        });
        if (response?.fault || (response?.title && response.type && response.detail)) {
            throw new Error(response.title);
        }

        api.auth?.saveBasketId(undefined);

        return response as Order;
    }

    /**
     * Fetches the applicable shipping methods for the current basket
     * @returns {Object} - API response containing data
     */
    function getShippingMethods(shipmentId = '') {
        if (!basket?.basketId || !shipmentId) return;
        return api.shopperBaskets
            ?.getShippingMethodsForShipment({
                parameters: { basketId: basket.basketId, shipmentId },
            })
            .catch(() => {
                if (translate('checkout.getShippingMethodError')) {
                    notifyError(new Error(translate('checkout.getShippingMethodError')));
                }
            });
    }

    return {
        ...basket,
        loaded,
        itemCount,
        itemAccumulatedCount,
        totalOrderDiscount,
        getOrCreateBasket,
        updateBasketCurrency,
        addItemToBasket,
        getProductsInBasket,
        getSelectedQuantity,
        updateItemInBasket,
        getProductInBasket,
        removeItemFromBasket,
        updateCustomerInfo,
        setBillingAddress,
        getShippingMethods,
        setShippingAddress,
        setShippingMethod,
        createOrder,
        getPromotions,
        applyPromoCode,
        removePromoCode,
        approvePaymentInstrument,
        addGiftCertificatePayment,
        removeGiftCertificatePayment,
        updateBasketTrackingIds,
        // addGiftCertificateItemToBasket,
        // removeGiftCertificateItemFromBasket,
    };
}
